/**
 * persistCollection
 *
 * Firebase operations for a Firestore collection.
 * Loads data via onSnapshot and provides write operations.
 *
 * Two failure rules hold per row (SYD-2413). A non-debounced `update` that
 * rejects never re-queues its own payload — the caller was told the write
 * failed, so a silent debounced retry would land it anyway. And a payload put
 * back into `updatePending` always has a live timer behind it, which is why
 * every restore goes through `armUpdateDebounce`: a pending with no timer keeps
 * that row's write queue blocked and defers its remote snapshots forever.
 */

import {
  collection,
  doc,
  endAt as firestoreEndAt,
  endBefore as firestoreEndBefore,
  limit as firestoreLimit,
  onSnapshot,
  orderBy as firestoreOrderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter as firestoreStartAfter,
  startAt as firestoreStartAt,
  Timestamp,
  updateDoc,
  addDoc,
  deleteDoc,
  where as firestoreWhere,
  type QueryConstraint,
  type QuerySnapshot,
  type UpdateData,
  type WithFieldValue,
} from 'firebase/firestore';
import debounce from 'lodash/debounce';
import { firestore } from '@/services/startFirestore';
import { SAVE_DEBOUNCE_MS } from '#types/System.Parameters.types';
import type { QueryFilter, QueryOptions, CollectionState, CollectionPersist, PersistOperationOptions } from '../Store.types';
import { mergeDocumentIntoProxy } from '../core/mergeDocumentIntoProxy';
import { queryOrderByKey } from '../core/queryOrderByKey';
import { createWriteQueue, type WriteQueue, type WriteQueueState } from './createWriteQueue';

const buildConstraintsFromFilters = (filters: QueryFilter[]): QueryConstraint[] => {
  return filters.map((f) => {
    switch (f.type) {
      case 'where': return firestoreWhere(f.field, f.op, f.value);
      case 'orderBy': return firestoreOrderBy(f.field, f.direction);
      case 'limit': return firestoreLimit(f.n);
      case 'startAt': return Array.isArray(f.cursor) ? firestoreStartAt(...f.cursor) : firestoreStartAt(f.cursor);
      case 'startAfter': return Array.isArray(f.cursor) ? firestoreStartAfter(...f.cursor) : firestoreStartAfter(f.cursor);
      case 'endAt': return Array.isArray(f.cursor) ? firestoreEndAt(...f.cursor) : firestoreEndAt(f.cursor);
      case 'endBefore': return Array.isArray(f.cursor) ? firestoreEndBefore(...f.cursor) : firestoreEndBefore(f.cursor);
    }
  });
};

type PersistCollectionResult<T> = {
  persist: CollectionPersist<T>;
  unsubscribe: () => void;
};

/**
 * Creates Firebase persistence operations for a collection.
 * Sets up onSnapshot to load data into the provided state.
 *
 * @param path - Firestore collection path
 * @param state - Valtio state to populate
 * @param onError - Optional error callback
 * @param queryOptions - Optional query constraints
 */
export function persistCollection<T extends Record<string, unknown>>(
  path: string,
  state: CollectionState<T>,
  onError?: (error: Error) => void,
  queryOptions?: QueryOptions,
  isActive?: () => boolean,
): PersistCollectionResult<T> {
  const collectionRef = collection(firestore, path);

  const orderByConstraint = queryOptions?.orderBy
    ? [firestoreOrderBy(queryOptions.orderBy.field, queryOptions.orderBy.direction)]
    : [];
  const extraConstraints = buildConstraintsFromFilters(queryOptions?.extraConstraints ?? []);

  const q = query(collectionRef, ...orderByConstraint, ...extraConstraints);

  let lastQuerySnapshot: QuerySnapshot | null = null;

  type DeferredSnapshot = { data: T };

  type DocWriteState = WriteQueueState<DeferredSnapshot> & {
    updateDebounced?: ReturnType<typeof debounce>;
    updatePending?: UpdateData<T>;
    updateInFlight?: UpdateData<T>;
  };

  type DocWriteQueue = WriteQueue<DeferredSnapshot> & {
    state: DocWriteState;
  };

  const docWriteQueueById = new Map<string, DocWriteQueue>();

  const applyDeferredSnapshot = (docId: string, deferred: DeferredSnapshot): void => {
    const existing = state.docs.get(docId);
    if (!existing) {
      state.docs.set(docId, { data: { ...deferred.data, docId } });
      if (!state.docIds.includes(docId)) {
        state.docIds.push(docId);
      }
      maybeDeleteDocWriteQueue(docId);
      return;
    }

    const localTime = ((existing.data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
    const incomingTime = ((deferred.data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
    if (incomingTime > 0 && localTime > 0 && incomingTime < localTime) {
      maybeDeleteDocWriteQueue(docId);
      return;
    }

    mergeDocumentIntoProxy(
      existing.data as Record<string, unknown>,
      deferred.data as Record<string, unknown>,
      docId
    );
    maybeDeleteDocWriteQueue(docId);
  };

  const getDocWriteQueue = (docId: string): DocWriteQueue => {
    const existing = docWriteQueueById.get(docId);
    if (existing) return existing;

    const queue = createWriteQueue<DeferredSnapshot>({
      onApplyDeferred: (deferred) => applyDeferredSnapshot(docId, deferred),
      onChainComplete: () => maybeDeleteDocWriteQueue(docId),
    }) as DocWriteQueue;

    docWriteQueueById.set(docId, queue);
    return queue;
  };

  const deleteDocWriteQueue = (docId: string): void => {
    const existing = docWriteQueueById.get(docId);
    if (!existing) return;
    existing.state.updateDebounced?.cancel();
    docWriteQueueById.delete(docId);
  };

  const maybeDeleteDocWriteQueue = (docId: string): void => {
    const queue = docWriteQueueById.get(docId);
    if (!queue) return;
    const queueState = queue.state;
    if (
      queueState.updatePending ||
      queueState.updateInFlight ||
      queueState.writing ||
      queueState.deferredSnapshot ||
      queueState.updateDebounced ||
      queueState.writeChain
    ) {
      return;
    }
    docWriteQueueById.delete(docId);
  };

  const isDocWriteBlocked = (docId: string): boolean => {
    const queue = docWriteQueueById.get(docId);
    if (!queue) return false;
    return queue.isBlocked();
  };

  const cancelUpdateDebounceAndPending = (docId: string): void => {
    const queue = docWriteQueueById.get(docId);
    if (!queue) return;
    queue.state.updateDebounced?.cancel();
    queue.state.updateDebounced = undefined;
    queue.state.updatePending = undefined;
    maybeDeleteDocWriteQueue(docId);
  };

  const unsubscribe = onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => {
      if (isActive && !isActive()) return;
      lastQuerySnapshot = snapshot;

      snapshot.docChanges().forEach((change) => {
        const docId = change.doc.id;

        if (change.type === 'removed') {
          state.docs.delete(docId);
          if (change.oldIndex >= 0 && state.docIds[change.oldIndex] === docId) {
            state.docIds.splice(change.oldIndex, 1);
          } else {
            const idx = state.docIds.indexOf(docId);
            if (idx !== -1) {
              state.docIds.splice(idx, 1);
            }
          }

          cancelNonUpdateDebouncedOps(docId);
          deleteDocWriteQueue(docId);
          return;
        }

        // Maintain ordered docIds using Firestore indices (supports reordering).
        if (change.type === 'added') {
          if (change.newIndex >= 0) {
            // Ensure no duplicates
            const existingIdx = state.docIds.indexOf(docId);
            if (existingIdx !== -1) {
              state.docIds.splice(existingIdx, 1);
            }
            state.docIds.splice(change.newIndex, 0, docId);
          } else if (!state.docIds.includes(docId)) {
            state.docIds.push(docId);
          }
        } else if (change.type === 'modified') {
          if (change.oldIndex !== change.newIndex && change.newIndex >= 0) {
            if (change.oldIndex >= 0 && state.docIds[change.oldIndex] === docId) {
              state.docIds.splice(change.oldIndex, 1);
            } else {
              const idx = state.docIds.indexOf(docId);
              if (idx !== -1) {
                state.docIds.splice(idx, 1);
              }
            }
            state.docIds.splice(change.newIndex, 0, docId);
          }
        }

        const data = change.doc.data() as T;
        const hasPendingWrites = (change.doc as unknown as { metadata?: { hasPendingWrites?: boolean } }).metadata
          ?.hasPendingWrites === true;

        const existing = state.docs.get(docId);
        if (existing) {
          if (isDocWriteBlocked(docId)) {
            if (hasPendingWrites) {
              mergeDocumentIntoProxy(
                existing.data as Record<string, unknown>,
                data as Record<string, unknown>,
                docId
              );
              return;
            }

            const queue = getDocWriteQueue(docId);
            const existingDeferred = queue.getDeferred();
            if (existingDeferred) {
              const existingTime = ((existingDeferred.data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
              const incomingTime = ((data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;

              if (incomingTime === 0 && existingTime > 0) {
                return;
              }

              if (incomingTime > 0 && existingTime > 0 && incomingTime <= existingTime) {
                return;
              }
            }

            queue.setDeferred({ data });
            return;
          }

          const localTime = ((existing.data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
          const incomingTime = ((data as Record<string, unknown>).modifiedAt as Timestamp | undefined)?.toMillis?.() ?? 0;
          if (incomingTime > 0 && localTime > 0 && incomingTime < localTime) {
            return;
          }

          mergeDocumentIntoProxy(
            existing.data as Record<string, unknown>,
            data as Record<string, unknown>,
            docId
          );
        } else {
          state.docs.set(docId, { data: { ...data, docId } });
        }
      });

      state.loading = false;
    },
    (error) => {
      if (isActive && !isActive()) return;
      state.loading = false;
      onError?.(error);
    }
  );

  const updateImmediate = async (docId: string, data: UpdateData<T>): Promise<void> => {
    const docRef = doc(firestore, path, docId);
    await updateDoc(docRef, { ...data, modifiedAt: serverTimestamp() });
  };

  /**
   * Drains one row's `updatePending` into a single `updateDoc`. Never rejects:
   * the debounce that owns it fires from a timer, so a rejection here would
   * surface as an unhandled one. A failure is reported through `onError` and the
   * payload is re-queued and re-armed until it lands.
   */
  const runPendingUpdate = async (id: string): Promise<void> => {
    const currentQueue = docWriteQueueById.get(id);
    if (!currentQueue) return;
    const currentState = currentQueue.state;

    try {
      if (currentState.updateInFlight) return;

      const merged = currentState.updatePending;
      if (!merged) return;

      currentState.updatePending = undefined;
      currentState.updateInFlight = merged;

      try {
        await currentQueue.enqueue(async () => {
          currentState.writing = true;
          try {
            await updateImmediate(id, merged);
          } finally {
            currentState.writing = false;
          }
        });
        currentState.updateInFlight = undefined;
      } catch (error) {
        currentState.updateInFlight = undefined;
        currentState.updatePending = currentState.updatePending
          ? ({ ...(merged as Record<string, unknown>), ...(currentState.updatePending as Record<string, unknown>) } as UpdateData<T>)
          : merged;
        throw error;
      }
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
    } finally {
      if (currentState.updatePending) {
        armUpdateDebounce(id);
      } else if (!currentState.updateInFlight && !currentState.writing) {
        currentState.updateDebounced?.cancel();
        currentState.updateDebounced = undefined;
        currentQueue.applyDeferredIfAny();
        maybeDeleteDocWriteQueue(id);
      }
    }
  };

  /**
   * Schedules a drain of one row's `updatePending`, creating the debounce if the
   * last run tore it down. Every path that leaves data in `updatePending` goes
   * through here: a payload with no timer behind it keeps that row's queue
   * blocked forever, and its remote snapshots are deferred for the rest of the
   * session.
   *
   * Reads the map directly rather than through `getDocWriteQueue`, so a row
   * whose queue a `removed` snapshot already deleted is never resurrected.
   */
  const armUpdateDebounce = (docId: string): void => {
    const queue = docWriteQueueById.get(docId);
    if (!queue) return;
    if (!queue.state.updateDebounced) {
      queue.state.updateDebounced = debounce(runPendingUpdate, SAVE_DEBOUNCE_MS);
    }
    queue.state.updateDebounced(docId);
  };

  const update = async (docId: string, data: UpdateData<T>, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    const queue = getDocWriteQueue(docId);
    const queueState = queue.state;

    if (shouldDebounce) {
      queueState.updatePending = queueState.updatePending
        ? { ...queueState.updatePending, ...data }
        : { ...data };

      armUpdateDebounce(docId);
    } else {
      cancelNonUpdateDebouncedOps(docId);

      const pending = queueState.updatePending;
      if (pending) {
        queueState.updatePending = undefined;
      }

      queueState.updateDebounced?.cancel();

      const merged = pending ? { ...pending, ...data } : { ...data };
      queueState.updateInFlight = merged;
      try {
        await queue.enqueue(async () => {
          queueState.writing = true;
          try {
            await updateImmediate(docId, merged);
          } finally {
            queueState.writing = false;
          }
        });
        queueState.updateInFlight = undefined;
      } catch (error) {
        queueState.updateInFlight = undefined;
        /**
         * Restores only the pending swept in at the start plus whatever another
         * call armed while this write was in flight — never this call's own
         * failed `data`. The caller rejects and rolls its proxy back, so a
         * silent debounced retry would land the write it just reported as
         * failed (SYD-2413).
         */
        queueState.updatePending = queueState.updatePending
          ? (pending
              ? ({ ...(pending as Record<string, unknown>), ...(queueState.updatePending as Record<string, unknown>) } as UpdateData<T>)
              : queueState.updatePending)
          : pending;
        throw error;
      } finally {
        if (queueState.updatePending) {
          /**
           * `armUpdateDebounce`, not `updateDebounced?.(docId)`: this branch
           * cancelled the debounce on the way in and a concurrent
           * `set`/`setMerge`/`delete` on the same id may have torn it down
           * entirely. A re-queued payload with no timer behind it wedges the row
           * permanently — its queue never drains and never gets deleted.
           */
          armUpdateDebounce(docId);
        } else if (!queueState.updateInFlight && !queueState.writing) {
          queueState.updateDebounced?.cancel();
          queueState.updateDebounced = undefined;
          queue.applyDeferredIfAny();
          maybeDeleteDocWriteQueue(docId);
        }
      }
    }
  };

  const setDebouncedMap = new Map<string, ReturnType<typeof debounce>>();
  const setMergeDebouncedMap = new Map<string, ReturnType<typeof debounce>>();
  const deleteDebouncedMap = new Map<string, ReturnType<typeof debounce>>();

  const cancelNonUpdateDebouncedOps = (docId: string): void => {
    const setDebounced = setDebouncedMap.get(docId);
    setDebounced?.cancel();
    setDebouncedMap.delete(docId);

    const setMergeDebounced = setMergeDebouncedMap.get(docId);
    setMergeDebounced?.cancel();
    setMergeDebouncedMap.delete(docId);

    const deleteDebounced = deleteDebouncedMap.get(docId);
    deleteDebounced?.cancel();
    deleteDebouncedMap.delete(docId);
  };

  const cancelPending = (): void => {
    for (const queue of docWriteQueueById.values()) {
      queue.state.updateDebounced?.cancel();
      queue.state.updateDebounced = undefined;
      queue.state.updatePending = undefined;
      queue.clearDeferred();
    }

    for (const debounced of setDebouncedMap.values()) {
      debounced.cancel();
    }
    setDebouncedMap.clear();

    for (const debounced of setMergeDebouncedMap.values()) {
      debounced.cancel();
    }
    setMergeDebouncedMap.clear();

    for (const debounced of deleteDebouncedMap.values()) {
      debounced.cancel();
    }
    deleteDebouncedMap.clear();
  };

  const flushPending = async (): Promise<void> => {
    const promises: Promise<unknown>[] = [];

    for (const queue of docWriteQueueById.values()) {
      if (queue.state.updateDebounced) {
        const result = queue.state.updateDebounced.flush();
        if (result instanceof Promise) promises.push(result);
      }
    }

    for (const debouncedFn of setDebouncedMap.values()) {
      const result = debouncedFn.flush();
      if (result instanceof Promise) promises.push(result);
    }

    for (const debouncedFn of setMergeDebouncedMap.values()) {
      const result = debouncedFn.flush();
      if (result instanceof Promise) promises.push(result);
    }

    for (const debouncedFn of deleteDebouncedMap.values()) {
      const result = debouncedFn.flush();
      if (result instanceof Promise) promises.push(result);
    }

    await Promise.allSettled(promises);
  };

  /**
   * Resolves `createdAt` in one precedence: the loaded document, then a
   * top-level `createdAt` on the payload, then now.
   *
   * The loaded document wins because it is the value the server holds, while a
   * payload carries whatever the caller assembled — a stale or hand-built
   * `createdAt` must never rewrite the creation time of a document already in
   * `state.docs`. The payload is consulted only when the document is absent
   * there, which is the shape of restoring a deleted document: without it the
   * write re-stamps a creation time the document never had. `modifiedAt` still
   * bumps in both cases, because the write is a genuine change.
   */
  const setImmediate = async (docId: string, data: T): Promise<void> => {
    const docRef = doc(firestore, path, docId);
    const loadedCreatedAt = (state.docs.get(docId)?.data as Record<string, unknown> | undefined)?.createdAt as Timestamp | undefined;
    const payloadCreatedAt = (data as Record<string, unknown>).createdAt as Timestamp | undefined;
    const createdAt = loadedCreatedAt ?? payloadCreatedAt ?? Timestamp.now();
    await setDoc(docRef, { ...data, createdAt, modifiedAt: serverTimestamp() });
  };

  const set = async (docId: string, data: T, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    if (shouldDebounce) {
      let debouncedFn = setDebouncedMap.get(docId);
      if (!debouncedFn) {
        debouncedFn = debounce(async (id: string, d: T) => {
          try {
            const queue = getDocWriteQueue(id);
            await queue.enqueue(async () => {
              queue.state.writing = true;
              try {
                await setImmediate(id, d);
              } finally {
                queue.state.writing = false;
              }
            });
          } catch (error) {
            onError?.(error instanceof Error ? error : new Error(String(error)));
          } finally {
            const queue = docWriteQueueById.get(id);
            queue?.applyDeferredIfAny();
          }
        }, SAVE_DEBOUNCE_MS);
        setDebouncedMap.set(docId, debouncedFn);
      }
      debouncedFn(docId, data);
    } else {
      cancelNonUpdateDebouncedOps(docId);
      cancelUpdateDebounceAndPending(docId);
      const queue = getDocWriteQueue(docId);
      await queue.enqueue(async () => {
        queue.state.writing = true;
        try {
          await setImmediate(docId, data);
        } finally {
          queue.state.writing = false;
        }
      });
      queue.applyDeferredIfAny();
    }
  };

  const setMergeImmediate = async (docId: string, data: Partial<T>): Promise<void> => {
    const docRef = doc(firestore, path, docId);
    await setDoc(docRef, { ...data, modifiedAt: serverTimestamp() }, { merge: true });
  };

  const setMerge = async (docId: string, data: Partial<T>, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    if (shouldDebounce) {
      let debouncedFn = setMergeDebouncedMap.get(docId);
      if (!debouncedFn) {
        debouncedFn = debounce(async (id: string, d: Partial<T>) => {
          try {
            const queue = getDocWriteQueue(id);
            await queue.enqueue(async () => {
              queue.state.writing = true;
              try {
                await setMergeImmediate(id, d);
              } finally {
                queue.state.writing = false;
              }
            });
          } catch (error) {
            onError?.(error instanceof Error ? error : new Error(String(error)));
          } finally {
            const queue = docWriteQueueById.get(id);
            queue?.applyDeferredIfAny();
          }
        }, SAVE_DEBOUNCE_MS);
        setMergeDebouncedMap.set(docId, debouncedFn);
      }
      debouncedFn(docId, data);
    } else {
      cancelNonUpdateDebouncedOps(docId);
      cancelUpdateDebounceAndPending(docId);
      const queue = getDocWriteQueue(docId);
      await queue.enqueue(async () => {
        queue.state.writing = true;
        try {
          await setMergeImmediate(docId, data);
        } finally {
          queue.state.writing = false;
        }
      });
      queue.applyDeferredIfAny();
    }
  };

  const addImmediate = async (data: WithFieldValue<T>): Promise<string> => {
    const docData = {
      ...data,
      modifiedAt: serverTimestamp(),
    };
    const docRef = await addDoc(collectionRef, docData);
    return docRef.id;
  };

  const add = async (data: WithFieldValue<T>, _options?: PersistOperationOptions): Promise<string> => {
    return await addImmediate(data);
  };

  const deleteImmediate = async (docId: string): Promise<void> => {
    const docRef = doc(firestore, path, docId);
    await deleteDoc(docRef);
  };

  const deleteDoc_ = async (docId: string, options?: PersistOperationOptions): Promise<void> => {
    const shouldDebounce = options?.debounce !== false;
    if (shouldDebounce) {
      let debouncedFn = deleteDebouncedMap.get(docId);
      if (!debouncedFn) {
        debouncedFn = debounce(async (id: string) => {
          try {
            const queue = getDocWriteQueue(id);
            await queue.enqueue(async () => {
              queue.state.writing = true;
              try {
                await deleteImmediate(id);
              } finally {
                queue.state.writing = false;
              }
            });
          } catch (error) {
            onError?.(error instanceof Error ? error : new Error(String(error)));
          } finally {
            const queue = docWriteQueueById.get(id);
            if (queue) {
              queue.clearDeferred();
              maybeDeleteDocWriteQueue(id);
            }
          }
        }, SAVE_DEBOUNCE_MS);
        deleteDebouncedMap.set(docId, debouncedFn);
      }
      debouncedFn(docId);
    } else {
      cancelNonUpdateDebouncedOps(docId);
      cancelUpdateDebounceAndPending(docId);
      const queue = getDocWriteQueue(docId);
      await queue.enqueue(async () => {
        queue.state.writing = true;
        try {
          await deleteImmediate(docId);
        } finally {
          queue.state.writing = false;
        }
      });
      queue.clearDeferred();
      maybeDeleteDocWriteQueue(docId);
    }
  };

  const persist: CollectionPersist<T> = {
    persistType: 'collection',
    collectionRef,
    query: q,
    orderByKey: queryOrderByKey(queryOptions?.orderBy),
    update,
    set,
    setMerge,
    add,
    delete: deleteDoc_,
    cancelPending,
    flushPending,
    getLastQuerySnapshot: () => lastQuerySnapshot,
  };

  return { persist, unsubscribe };
}
